import { useAppRouterInterface } from '@/app/addRouter';
import { DrillDownNode, DrillDownPath } from '@/app/navigation';
import { useIsAllowed } from '@/app/navigation/access';
import { NodeMeta } from '@/app/routes';
import { ErrorPanel } from '@/components/Layout/Panel/ErrorPanel';
import { ForbiddenPanel } from '@/components/Layout/Panel/ForbiddenPanel';
import { PanelContextProvider } from '@/components/Layout/Panel/PanelContext';
import {
  StackContext,
  StackContextRef,
} from '@/components/Layout/Stack/StackContext';
import {
  ViewContext,
  ViewContextType,
} from '@/components/Layout/View/ViewContext';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import intersperseWith from '@/lib/intersperse';
import { cn } from '@/lib/utils';
import { Maximize, Minimize, X } from 'lucide-react';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const STACK_MAXIMIZE_COOKIE_NAME = 'stack_maximize_state';
const STACK_MAXIMIZE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10; // 10 years (effectively permanent)

interface StackProps extends React.ComponentProps<'div'> {
  panelCnt?: number;
  drillDownPath: DrillDownPath<NodeMeta>;
  manageDocumentTitle?: boolean;
  stackIdx: number;
}

const Stack: React.FC<StackProps> = ({
  drillDownPath,
  panelCnt = 1,
  manageDocumentTitle = false,
  stackIdx,
  className,
  ...props
}) => {
  const viewContextMap = React.useRef(new Map<string, ViewContextType>());
  const stackCtxRef = useRef({ stackIdx });
  stackCtxRef.current = { stackIdx };

  // Read the maximize state from cookie on initialization
  // TODO: scalable abstraction for settings
  const getInitialMaximizeState = useCallback(() => {
    if (typeof document === 'undefined') return false;
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${STACK_MAXIMIZE_COOKIE_NAME}=`))
      ?.split('=')[1];
    return cookieValue ? cookieValue === 'true' : false;
  }, []);

  const [_maximize, _setMaximize] = useState(getInitialMaximizeState);

  const setMaximize = useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const maximizeState =
        typeof value === 'function' ? value(_maximize) : value;
      _setMaximize(maximizeState);

      // Save the maximize state to cookie
      document.cookie = `${STACK_MAXIMIZE_COOKIE_NAME}=${maximizeState}; path=/; max-age=${STACK_MAXIMIZE_COOKIE_MAX_AGE}`;
    },
    [_maximize],
  );

  const maximize = _maximize;
  const [, _forceUpdate] = useState(0);
  const forceUpdate = () => _forceUpdate((n) => n + 1);
  const forceMaximized = drillDownPath.length === 1 || panelCnt === 1;
  const calculatedPanelCnt = maximize ? 1 : panelCnt;

  const appRouter = useAppRouterInterface();

  for (const [idx, match] of drillDownPath.entries()) {
    const key = match.path;
    const ctx = viewContextMap.current.get(key);
    if (!ctx) {
      let title = '';
      // Determine if this panel will be rendered or just needs title
      const isVisible = idx >= drillDownPath.length - calculatedPanelCnt;
      viewContextMap.current.set(key, {
        get title() {
          return title || match.node.label;
        },
        setTitle(newTitle: string) {
          title = newTitle;
          forceUpdate();
        },
        panelIdx: idx, // Add panel index for URL state management
        renderMode: isVisible ? 'full' : 'title-only',
      });
    } else {
      // Update renderMode if panel visibility changed
      const isVisible = idx >= drillDownPath.length - calculatedPanelCnt;
      ctx.renderMode = isVisible ? 'full' : 'title-only';
    }
  }

  if (manageDocumentTitle) {
    const lastPath = drillDownPath.at(-1)?.path;
    document.title = lastPath
      ? viewContextMap.current.get(lastPath)?.title || 'Certhon Web'
      : 'Certhon Web';
  }

  // Breadcrumbs: show all items when the container is wide enough,
  // collapse early items to "..." on narrow containers via @container queries.
  const hasEarlyItems = drillDownPath.length > 2;
  const earlyItems = drillDownPath.slice(0, -2);
  const lastTwoItems = drillDownPath.slice(-2);

  const breadcrumbs = (
    <>
      {/* Early items — hidden on narrow containers, shown on wide */}
      {hasEarlyItems && (
        <>
          <span className="hidden @3xl:contents">
            {intersperseWith(
              (idx) => (
                <BreadcrumbSeparator key={`e-sep-${idx}`} />
              ),
              earlyItems.map(({ path: url }) => {
                const title =
                  viewContextMap.current.get(url)?.title || '$%##!@&^*';
                return (
                  <BreadcrumbItem key={url}>
                    <BreadcrumbLink to={url}>{title}</BreadcrumbLink>
                  </BreadcrumbItem>
                );
              }),
            )}
            <BreadcrumbSeparator key="e-sep-last" />
          </span>
          {/* Ellipsis — shown on narrow, hidden on wide */}
          <BreadcrumbItem key="ellipsis" className="@3xl:hidden">
            <span className="text-muted-foreground">...</span>
          </BreadcrumbItem>
          <BreadcrumbSeparator key="ellipsis-sep" className="@3xl:hidden" />
        </>
      )}
      {/* Last two items — always visible */}
      {intersperseWith(
        (idx) => (
          <BreadcrumbSeparator key={`l-sep-${idx}`} />
        ),
        lastTwoItems.map(({ path: url }, idx) => {
          const isLast = idx === lastTwoItems.length - 1;
          const title = viewContextMap.current.get(url)?.title || '$%##!@&^*';
          return (
            <BreadcrumbItem key={url} className={isLast ? 'min-w-0' : ''}>
              {isLast ? (
                <BreadcrumbPage className="min-w-0 truncate">
                  <span className="md:hidden">...</span>
                  <span className="hidden md:inline">{title}</span>
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink className="shrink-0 text-nowrap" to={url}>
                  {title}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          );
        }),
      )}
    </>
  );

  return (
    <StackContext.Provider value={{ stackIdx }}>
      <StackContextRef.Provider value={stackCtxRef}>
        <div
          {...props}
          className={cn(
            'group/stack @container mb-2 flex grow flex-col px-1 py-0 md:px-2',
            className,
          )}
        >
          <header
            className={cn(
              'flex shrink-0 flex-col p-2 transition-[width,height] ease-linear',
              // 'group-has-[[data-collapsible=icon]]/sidebar-wrapper:min-h-24',
              //showSearchBar
              //   ? 'min-h-16 gap-2 md:min-h-20':
              'min-h-10 md:min-h-12',
              drillDownPath.length <= 1 && 'hidden md:flex',
            )}
          >
            {/* Top row: Navigation */}
            <div className="group/top flex w-full items-center justify-between">
              <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                <Breadcrumb className="min-w-0">
                  <BreadcrumbList className="min-w-0 flex-nowrap">
                    {breadcrumbs}
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <div
                className="hidden opacity-0 transition-opacity group-hover/stack:opacity-30
                  group-hover/top:opacity-100 md:block"
              >
                {/* Primary stack: maximize toggle (show 1 vs 2 panels) */}
                {stackIdx === 0 && (
                  <Button
                    tooltip="Maximize last stack-panel"
                    variant="ghost"
                    size="icon"
                    disabled={forceMaximized}
                    className="text-muted-foreground size-8"
                    onClick={() => {
                      setMaximize((maximize) => !maximize);
                    }}
                  >
                    {forceMaximized || maximize ? <Maximize /> : <Minimize />}
                  </Button>
                )}
                {/* Drawer stack: close button */}
                {stackIdx !== 0 && (
                  <Button
                    tooltip="Close side panel"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground size-8"
                    onClick={() => {
                      appRouter.dropStack(stackIdx);
                    }}
                  >
                    <X />
                  </Button>
                )}
              </div>
            </div>
          </header>
          <section className="relative flex items-start gap-x-2">
            {drillDownPath.map(({ node, path, base, params }, idx) => {
              const viewContext = viewContextMap.current.get(path);
              if (!viewContext) {
                throw new Error(`View context not found for path: ${path}`);
              }
              let width: 'collapsed' | 'compact' | 'wide' | 'full' =
                'collapsed';
              if (idx >= drillDownPath.length - calculatedPanelCnt) {
                if (forceMaximized || maximize) {
                  width = 'full';
                } else if (idx == drillDownPath.length - 1) {
                  width = 'full';
                } else {
                  width = 'compact';
                }
              }
              const parentParams = drillDownPath[idx - 1]?.params ?? {};
              return (
                <PanelWrapper
                  key={base}
                  {...{
                    viewContext,
                    idx,
                    stackIdx,
                    drillDownPath,
                    calculatedPanelCnt,
                    node,
                    params,
                    parentParams,
                    path,
                    base,
                    width,
                  }}
                />
              );
            })}
          </section>
        </div>
      </StackContextRef.Provider>
    </StackContext.Provider>
  );
};

export default memo(Stack);

// Memoized wrapper to prevent unnecessary re-renders of panels
const PanelWrapper = memo(
  ({
    viewContext,
    idx,
    stackIdx,
    drillDownPath,
    calculatedPanelCnt,
    node,
    params,
    parentParams,
    path,
    base,
    width,
  }: {
    viewContext: ViewContextType;
    idx: number;
    stackIdx: number;
    drillDownPath: DrillDownPath<NodeMeta>;
    calculatedPanelCnt: number;
    node: DrillDownNode<NodeMeta, string>;
    params: Record<string, string>;
    parentParams: Record<string, string>;
    path: string;
    base: string;
    width: 'collapsed' | 'compact' | 'wide' | 'full';
  }) => {
    const isVisible = idx >= drillDownPath.length - calculatedPanelCnt;

    // The node and every ancestor must be open to this account; otherwise a
    // placeholder takes the panel's slot and its queries never start.
    const access = useIsAllowed();
    const Panel = access.isPathAllowed(drillDownPath.slice(0, idx + 1))
      ? node.Panel
      : ForbiddenPanel;

    // TODO: this should be default behaviour of error boundary
    const handleError = (
      error: Error,
      info: { componentStack?: string | null },
    ) => {
      console.error('Panel Error:', error);
      if (info.componentStack) {
        console.error('Component Stack:', info.componentStack);
      }
    };

    const errorFallback = ({
      error,
      resetErrorBoundary,
    }: {
      error: Error;
      resetErrorBoundary: () => void;
    }) => (
      <ErrorPanel
        error={error}
        resetErrorBoundary={resetErrorBoundary}
        title={viewContext.title}
        icon={node.icon}
        path={path}
        width={width}
        maxPanelWidth={node.maxPanelWidth}
        hidden={!isVisible}
      />
    );

    return (
      <ErrorBoundary fallbackRender={errorFallback} onError={handleError}>
        <PanelContextProvider stackIdx={stackIdx} panelIdx={idx}>
          <ViewContext.Provider value={viewContext}>
            <Panel
              params={params}
              parentParams={parentParams}
              path={path}
              basePath={base}
              idx={idx}
              title={viewContext.title}
              icon={node.icon}
              width={width}
              maxPanelWidth={node.maxPanelWidth}
              hidden={!isVisible}
            />
          </ViewContext.Provider>
        </PanelContextProvider>
      </ErrorBoundary>
    );
  },
);
